import React from "react";
import useEntity from "../../../hooks/usePrice";
import {
    Box, 
    Paper, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Link,
    TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import {ID} from "../../../../types/global.d";
import EntityListHeader from "./PriceListHeader";
import EntityListSearch from "./PriceListSearch";
import {useLocation} from "react-router";
import PriceListPagination from "./PriceListPagination";
import * as Api from "../../../apis/Price.api";
import { Price } from "../../../types/models/Price";
import { PrimaryPrice } from "../../../types/models/Price";
import { usePagination } from '../../../../share';
import {displayDateTime, displayDate, displayTime} from "../../../../utils/formatDate.ts";
import {percentageFormat, fixedFormat, currencyFormat} from "../../../../utils/numberFormat";

import PricingProfileLink from "../../../../timber/components/pricingProfile/PricingProfileDetail/PricingProfileLink";
import PricingProfileName from "../../../../timber/components/pricingProfile/PricingProfileDetail/PricingProfileName";
import TimberTypeLink from "../../../../timber/components/timberType/TimberTypeDetail/TimberTypeLink";
import TimberTypeName from "../../../../timber/components/timberType/TimberTypeDetail/TimberTypeName";
import TimberClassLink from "../../../../timber/components/timberClass/TimberClassDetail/TimberClassLink";
import TimberClassName from "../../../../timber/components/timberClass/TimberClassDetail/TimberClassName";
import ThicknessClassLink from "../../../../timber/components/thicknessClass/ThicknessClassDetail/ThicknessClassLink";
import ThicknessClassName from "../../../../timber/components/thicknessClass/ThicknessClassDetail/ThicknessClassName";
import { toStringDisplay as toStringCuttingState } from "../../../types/models/CuttingState.d";
import { toStringDisplay as toStringShipmentState } from "../../../types/models/ShipmentState.d";
import { toStringDisplay as toStringShipmentType } from "../../../types/models/ShipmentType.d";
import { toStringDisplay as toStringUserType } from "../../../types/models/UserType.d";
import { toStringDisplay as toStringCordwoodMeasurementType } from "../../../types/models/CordwoodMeasurementType.d";

import { PrimaryPricingProfile } from "../../../../timber/types/models/PricingProfile";
import {useTheme} from "@mui/material/styles";
import useSearchDebounce from "../../../../hooks/useSearchDebounce";
import PriceListItemTools from "./PriceListItemTools";
import {useStateSession} from "../../../../hooks/useAuth";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans } from "@lingui/react/macro";
import {
    mdiLinkVariant,
} from "@mdi/js";
import {
    StyledFillNavLink,
    ToolbarTableCell,
    BigTableRow,
    BigTableHeaderRow,
    SmallTableRow,
    SmallTableHeaderRow,
    StyledTableCell,
    StyledMdiIcon,
} from "../../../../share/components/styled/Table";
import {OrderBy, OrderDirection} from "../../../../types/global.d";

type IProps = {
    showAdd: boolean,
    timberTypeId?: ID,
    classId?: ID,
    thicknessClassId?: ID,
    pricingProfileId?: ID,
    };

const PriceListItem = (props: IProps) => {
    
    const session = useStateSession()
    const {showAdd, ...query} = props;

    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('Price')

    
    
    
    
    const [, setSearchInputValue, searchInputTerm] = useSearchDebounce();
    const [searchInputAdvanced, setSearchInputAdvanced] = React.useState(undefined);

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    const { data: dataSimple = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
    }, 
        isLoading: isLoadingSimple,
        isFetching: isFetchingSimple,
        refetch: refetchSimple,
    } =
          Api.useGetPricePresentationQuery({
                  list: { page, pageSize },
                  filter: {
                    pricingProfileIdRefs: (PrimaryPricingProfile.fromQuery(query.pricingProfileId)) ? [ PrimaryPricingProfile.fromQuery(query.pricingProfileId) ] : [],
                    // for now not supported to query here other types
                    timberTypeId: null,
                    // for now not supported to query here other types
                    classId: null,
                    // for now not supported to query here other types
                    thicknessClassId: null,
                    orderBys,
                  },
              }, {skip: !!searchInputAdvanced})
    
    const { data: dataAdvanced = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
        }, 
        isLoading: isLoadingAdvanced,
        isFetching: isFetchingAdvanced,
        refetch: refetchAdvanced,
    } =
        Api.useGetPriceAdvancedPresentationQuery({
                list: { page, pageSize },
                filter: {

                    pricingProfileIdRefs: (PrimaryPricingProfile.fromQuery(query.pricingProfileId)) ? [ PrimaryPricingProfile.fromQuery(query.pricingProfileId) ] : [],
                    ...searchInputAdvanced,
                    orderBys,
                },
            }, {skip: !searchInputAdvanced})
    const {data, isLoading, isFetching, refetch} =
        searchInputAdvanced ? 
            {
                data: dataAdvanced,
                isLoading: isLoadingAdvanced,
                isFetching: isFetchingAdvanced,
                refetch: refetchAdvanced,
            } 
            :
        {
                data: dataSimple,
                isLoading: isLoadingSimple,
                isFetching: isFetchingSimple,
                refetch: refetchSimple,
            }

    const routeLocation = useLocation();
    
    const onClickUrl = (entity: Price) => routeLocation.pathname.endsWith('prices') ?
        `${PrimaryPrice.fromEntity(entity).toPath()}`
        :
        `prices/${PrimaryPrice.fromEntity(entity).toPath()}`

    const onEditUrl = (entity: Price) => `${onClickUrl(entity)}/edit`

    
    
    return React.useMemo(() => (
        <Box>
            <Paper>
                <EntityListHeader showAdd={showAdd} {...query}/>
                <EntityListSearch
                    setSearchInputValue={setSearchInputValue}
                    searchInputAdvanced={searchInputAdvanced} setSearchInputAdvanced={setSearchInputAdvanced}
                    query={query}
                    refetch={refetch}
                />
                <TableContainer>

                    


                    

                    

                    

                    
        
                    
                        <Table stickyHeader data-testid="Price-entity-list" size="small">
                            <TableHead>
                                <BigTableHeaderRow>
                                    
                                    
                                    
                                    
                                    
                                    { query.timberTypeId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'timberTypeId') }
                                                direction={ orderBys.find((item) => item.name === 'timberTypeId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('timberTypeId')}
                                            >
                                            
                                                <Trans>Timber Type</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    { query.classId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'classId') }
                                                direction={ orderBys.find((item) => item.name === 'classId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('classId')}
                                            >
                                            
                                                <Trans>Timber Class</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    { query.thicknessClassId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'thicknessClassId') }
                                                direction={ orderBys.find((item) => item.name === 'thicknessClassId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('thicknessClassId')}
                                            >
                                            
                                                <Trans>Thickness Class</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="right">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'price') }
                                            direction={ orderBys.find((item) => item.name === 'price')?.direction || 'asc' }
                                            onClick={() => toggleOrder('price')}
                                        >
                                        
                                            <Trans>Price</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    
                                    
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryPrice.fromEntity(item).toPath() }
                                    >
                                        
                                        
                                        
                                        
                                        
                        { query.timberTypeId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberTypeLink id={ item.timberTypeId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        { query.classId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <TimberClassLink id={ item.classId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        { query.thicknessClassId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <ThicknessClassLink id={ item.thicknessClassId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="right"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {fixedFormat(item.price) || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                                        
                                        <ToolbarTableCell align="center" width="15%">
                                            <PriceListItemTools data={ item }
                                                                               onClickUrl={onClickUrl(item)} onEditUrl={onEditUrl(item)}
                                                                                />
                                        </ToolbarTableCell>
                                    </BigTableRow>
                                ))}
                            </TableBody>
                        </Table>
            
                    { !data.totalItems && !isLoading && !isFetching &&
                        <Typography
                            sx={ { flex: '1 1 100%', padding: "2em", } }
                            component="div"
                        >
                            <Trans>No Prices</Trans>
                        </Typography>
                    }
                    { (isLoading || isFetching) &&
                        <GradientCircularProgress/>
                    }
                </TableContainer>
                <PriceListPagination
                    totalItems={ data.totalItems }
                    page={Number(page)}
                    pageSize={pageSize}
                    handleChangePage={handleChangePage}
                    handleChangePageSize={handleChangePageSize}
                />
            </Paper>
        </Box>
    ), [isLoading, isFetching, data, page, pageSize, onClickUrl,  onEditUrl])
}

export default PriceListItem;
